import { useEffect } from 'react'
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form'

import type { LoadboardDefaultResponse } from '#/shared/api/generated/loadboard/models'
import { useGetDefaultLoadboardApiV1LoadboardsDefaultGet } from '#/shared/api/generated/loadboard/theCargoLoadboard'

import { DEFAULT_BALANCE_SELECTION } from './payment-options'

const SET_OPTIONS = { shouldDirty: false, shouldValidate: true } as const

const DEFAULT_METHOD_FIELDS = [
  'paymentMethod',
  'paymentTerms',
  'balanceMethod',
  'balanceTime',
  'balanceTermBegins',
] as const

type DefaultMethodField = (typeof DEFAULT_METHOD_FIELDS)[number]

function buildSelection(
  preset: LoadboardDefaultResponse | undefined,
): Record<DefaultMethodField, string | null | undefined> {
  return {
    paymentMethod: preset?.method,
    paymentTerms: preset?.terms,
    balanceMethod:
      preset?.balance_payment_method ?? DEFAULT_BALANCE_SELECTION.balanceMethod,
    balanceTime:
      preset?.balance_payment_time ?? DEFAULT_BALANCE_SELECTION.balanceTime,
    balanceTermBegins:
      preset?.balance_terms_begin_on ??
      DEFAULT_BALANCE_SELECTION.balanceTermBegins,
  }
}

/**
 * Fills the payment method/terms selects from the account's default loadboard
 * preset, but only for fields the user hasn't already set. Gated by the
 * `payment.defaultMethods` manage-fields toggle (passed in as `enabled`), so it
 * runs in any payment form — order create/edit, the detail sidebar, and the
 * full detail page.
 */
export function useDefaultMethodsAutofill<T extends FieldValues>(
  enabled: boolean,
  getValues: UseFormGetValues<T>,
  setValue: UseFormSetValue<T>,
) {
  const { data } = useGetDefaultLoadboardApiV1LoadboardsDefaultGet({
    query: { enabled },
  })
  const preset = data?.data

  useEffect(() => {
    if (!enabled) return
    const selection = buildSelection(preset)
    for (const name of DEFAULT_METHOD_FIELDS) {
      const value = selection[name]
      const field = name as Path<T>
      if (value && !getValues(field)) {
        setValue(field, value as PathValue<T, Path<T>>, SET_OPTIONS)
      }
    }
  }, [enabled, preset, getValues, setValue])
}
